import { BookedQuote } from "../providers/bookedQuotesProvider";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Bundle, Product } from "../providers/catalogProvider";
import { Money } from "./Money";
import DialogContentText from "@mui/material/DialogContentText";

export function PricesOverview({
  bookedQuote,
  newBundle,
}: { bookedQuote: BookedQuote; newBundle?: Bundle } | { bookedQuote?: undefined; newBundle: Bundle }) {
  const oldPrices = bookedQuote
    ? Object.fromEntries(
        Object.entries(bookedQuote.prices).map(([label, cents]) => [
          label === "Grundgebühr" ? "Grundbetrag" : label,
          <Money cents={cents} />,
        ]),
      )
    : undefined;
  const newPrices = newBundle ? pricesOf(newBundle) : undefined;
  let labels = [...new Set([...Object.keys(oldPrices ?? {}), ...Object.keys(newPrices ?? {})])].sort((a, b) =>
    a.localeCompare(b, "de"),
  );
  // "Grundbetrag" should be the first label ...
  {
    const i = labels.indexOf("Grundbetrag");
    if (i > 0) {
      labels.splice(0, 0, ...labels.splice(i, 1));
    }
  }
  const comparingPrices = !!oldPrices && !!newPrices;
  const oldBaseFee = bookedQuote?.prices.Grundbetrag ?? bookedQuote?.prices.Grundgebühr;
  const newBaseFee = newBundle ? newBundle.baseFee * 100 : undefined;
  return (
    <>
      {comparingPrices && oldBaseFee !== newBaseFee && (
        <DialogContentText sx={{ mt: "1em", fontStyle: "italic" }}>
          Hinweis: Änderungen am Grundbetrag werden nicht automatisch eingezogen. Sollte dies nötig sein, bitte eine
          manuelle Rechnung erstellen.
        </DialogContentText>
      )}
      <TableContainer sx={{ width: "100%" }}>
        <Table sx={{ width: "100%" }}>
          {comparingPrices && (
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="right">{"alter\u00A0Preis"}</TableCell>
                <TableCell align="right">{"neuer\u00A0Preis"}</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {labels.map((label) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                {oldPrices && <TableCell align="right">{oldPrices[label] ?? "---"}</TableCell>}
                {newPrices && <TableCell align="right">{newPrices[label] ?? "---"}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function pricesOf(bundle: Bundle): Record</* label: */ string, any> {
  const prices: Record<string, any> = {
    Grundbetrag: <Money cents={bundle.baseFee * 100} />,
  };
  if (bundle.teachingMaterialFee) {
    prices["Lehrmittelgebühr"] = <Money cents={bundle.teachingMaterialFee * 100} />;
  }
  const products = productsOf(bundle);
  for (const [id, product] of products.entries()) {
    // This is the same code used in the bookedTrainingPrices function in microservices/.../data-sync.service.ts
    let label = product.name;
    if (
      product.type === "Übungsstunde" ||
      product.type === "Autobahnfahrt" ||
      product.type === "Nachtfahrt" ||
      product.type === "Überlandfahrt"
    ) {
      label += " (45 min)";
    }
    prices[label] = <Money cents={bundle.productPrices[id] * 100} />;
  }
  for (const [productId, price] of Object.entries(bundle.authorityFees)) {
    const product = products.get(productId);
    if (product) {
      // This is the same code used in the bookedTrainingPrices function in microservices/.../data-sync.service.ts
      const label = `Prüfungsgebühr (TÜV/DEKRA) für ${product.type}`;
      prices[label] = <Money cents={price * 100} />;
    }
  }
  return prices;
}

function productsOf(bundle: Bundle): Map<string, Product> {
  const result = new Map<string, Product>();
  for (const training of bundle.trainings) {
    for (const product of training.products) {
      result.set(product.id, product);
    }
  }
  for (const product of bundle.products) {
    result.set(product.id, product);
  }
  return result;
}
